import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { getSyncProgressState, reset } from '~/data/sync/syncProgressState';
import { SyncProgressStatus } from '~/data/sync/syncProgressTypes';
import { useSyncProgress } from '~/data/sync/useSyncProgress';

import SyncProgressDialog from './SyncProgressDialog';
import SyncStatusChip, { SyncStatusChipProps } from './SyncStatusChip';

type SyncProgressUiContextValue = {
  chipProps: SyncStatusChipProps;
  dialogOpen: boolean;
  onDialogClose: () => void;
};

const SyncProgressUiContext = createContext<SyncProgressUiContextValue | null>(null);

function useSyncProgressUiController(): SyncProgressUiContextValue {
  const { status } = useSyncProgress();
  const [minimized, setMinimized] = useState(false);
  const prevStatusRef = useRef<SyncProgressStatus | null>(null);

  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;

    if (prev === null) {
      return;
    }

    if (
      status === 'syncing' &&
      (prev === 'idle' || prev === 'success' || prev === 'partial_failure')
    ) {
      setMinimized(false);
    }
  }, [status]);

  const handleDialogClose = useCallback(() => {
    const current = getSyncProgressState().status;

    if (current === 'success' || current === 'partial_failure') {
      reset();
      setMinimized(false);
      return;
    }

    if (current === 'syncing' || current === 'paused') {
      setMinimized(true);
    }
  }, []);

  const handleChipAutoHide = useCallback(() => {
    if (getSyncProgressState().status === 'success') {
      reset();
      setMinimized(false);
    }
  }, []);

  const handleOpenDialog = useCallback(() => {
    setMinimized(false);
  }, []);

  const dialogOpen = status !== 'idle' && !minimized;
  const chipVisible = minimized && status !== 'idle';

  return {
    chipProps: {
      visible: chipVisible,
      onOpenDialog: handleOpenDialog,
      onAutoHide: handleChipAutoHide,
    },
    dialogOpen,
    onDialogClose: handleDialogClose,
  };
}

export const SyncProgressUiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const value = useSyncProgressUiController();

  return (
    <SyncProgressUiContext.Provider value={value}>
      {children}
      <SyncProgressDialog open={value.dialogOpen} onClose={value.onDialogClose} />
    </SyncProgressUiContext.Provider>
  );
};

/** Renders the background sync strip; mount inside the app message banner stack. */
export const SyncProgressBanner: React.FC = () => {
  const value = useContext(SyncProgressUiContext);

  if (!value) {
    return null;
  }

  return <SyncStatusChip {...value.chipProps} />;
};

/**
 * Single mount point for sync progress UI: dialog (portaled) + background chip.
 * Prefer {@link SyncProgressUiProvider} + {@link SyncProgressBanner} in App layout.
 */
const SyncProgressUi: React.FC = () => {
  const value = useSyncProgressUiController();

  return (
    <>
      <SyncStatusChip {...value.chipProps} />
      <SyncProgressDialog open={value.dialogOpen} onClose={value.onDialogClose} />
    </>
  );
};

export default SyncProgressUi;
